import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { ErreurApi, appelApi } from '../api/client';
import type {
  Fiche,
  GroupeVue,
  LigneVue,
  NoteVue,
  Progression,
  ResultatActionMasse,
  ResultatEcriture,
  StatutNote,
} from '../api/types';
import { useSession } from '../auth/session';
import {
  Anneau,
  Badge,
  Chargement,
  Jauge,
  Message,
  dateLongue,
  joursRestants,
} from '../composants/elements';

/**
 * L'écran de saisie — §6.1 de la spec.
 *
 * Ce qui rend 304 lignes tenable, par ordre d'importance : le clavier de bout en
 * bout, le report du trimestre précédent, le « non concerné » en masse, les
 * nouveautés épinglées, et l'enregistrement à la ligne. Tout est ici.
 */

const PREUVE_MIN = 20;
const SEUIL_PREUVE = 3;

type EtatSauvegarde = 'repos' | 'en_cours' | 'enregistre' | 'preuve_attendue' | 'erreur';

type Etat = { etat: EtatSauvegarde; message?: string };

function preuveSuffisante(preuve: string): boolean {
  return preuve.trim().length >= PREUVE_MIN;
}

function noteIncomplete(note: NoteVue | null, brouillon: string | undefined): boolean {
  if (!note) return false;
  if (note.statut !== 'notee' || note.niveau === null) return false;
  if (note.niveau < SEUIL_PREUVE) return false;
  return !preuveSuffisante(brouillon ?? note.preuve ?? '');
}

export function MaFiche(): JSX.Element {
  const session = useSession();
  const params = useParams<{ trimestre?: string }>();
  const [recherche, setRecherche] = useSearchParams();

  const trimestre = params.trimestre ?? session.moi?.campagneOuverte?.trimestre ?? null;

  const [fiche, setFiche] = useState<Fiche | undefined>(undefined);
  const [erreurChargement, setErreurChargement] = useState<string | undefined>(undefined);
  const [groupeActif, setGroupeActif] = useState<string | null>(null);
  const [brouillons, setBrouillons] = useState<Record<string, string>>({});
  const [etats, setEtats] = useState<Record<string, Etat>>({});
  const [filtre, setFiltre] = useState('');
  const [indexFocus, setIndexFocus] = useState(0);
  const [soumission, setSoumission] = useState<string | undefined>(undefined);
  const [actionMasse, setActionMasse] = useState<string | undefined>(undefined);

  const champRecherche = useRef<HTMLInputElement | null>(null);
  const grille = useRef<HTMLElement | null>(null);
  const lignesRef = useRef<(HTMLDivElement | null)[]>([]);
  const preuvesRef = useRef<Record<string, HTMLTextAreaElement | null>>({});
  const minuteurs = useRef<Record<string, number>>({});

  // --- Chargement ---------------------------------------------------------

  const charge = useCallback(async () => {
    if (!trimestre) return;
    try {
      const resultat = await appelApi<Fiche>(`/api/campagnes/${trimestre}/ma-fiche`);
      setFiche(resultat);
      setBrouillons(
        Object.fromEntries(
          resultat.groupes.flatMap((g) =>
            g.technologies
              .filter((t) => t.note?.preuve)
              .map((t) => [t.technologieId, t.note!.preuve!]),
          ),
        ),
      );
      setGroupeActif((actuel) => actuel ?? resultat.groupes[0]?.cle ?? null);
    } catch (cause) {
      setErreurChargement(cause instanceof Error ? cause.message : 'Could not load your profile');
    }
  }, [trimestre]);

  useEffect(() => {
    void charge();
  }, [charge]);

  // Un lien « /ma-fiche?radar=ai » ouvre directement le premier groupe du radar.
  useEffect(() => {
    const radar = recherche.get('radar');
    if (!radar || !fiche) return;
    const premier = fiche.groupes.find((g) => g.radar === radar);
    if (premier) setGroupeActif(premier.cle);
    recherche.delete('radar');
    setRecherche(recherche, { replace: true });
  }, [fiche, recherche, setRecherche]);

  // --- Écriture -----------------------------------------------------------

  const majNote = useCallback((technologieId: string, note: NoteVue | null, progression?: Progression) => {
    setFiche((actuelle) => {
      if (!actuelle) return actuelle;
      return {
        ...actuelle,
        progression: progression ?? actuelle.progression,
        groupes: actuelle.groupes.map((g) => ({
          ...g,
          technologies: g.technologies.map((t) =>
            t.technologieId === technologieId ? { ...t, note } : t,
          ),
        })),
      };
    });
  }, []);

  const enregistre = useCallback(
    async (ligne: LigneVue, statut: StatutNote, niveau: number | null, preuve: string | null) => {
      if (!trimestre) return;
      const id = ligne.technologieId;
      setEtats((e) => ({ ...e, [id]: { etat: 'en_cours' } }));

      try {
        const resultat = await appelApi<ResultatEcriture>(
          `/api/campagnes/${trimestre}/notes/${id}`,
          { methode: 'PUT', corps: { statut, niveau, preuve } },
        );
        majNote(id, resultat.note, resultat.progression);
        setEtats((e) => ({ ...e, [id]: { etat: 'enregistre' } }));
      } catch (cause) {
        const message =
          cause instanceof ErreurApi
            ? (cause.details?.anomalies?.[0]?.message ?? cause.message)
            : 'Could not save';
        setEtats((e) => ({ ...e, [id]: { etat: 'erreur', message } }));
      }
    },
    [trimestre, majNote],
  );

  /**
   * Choisir un niveau. À partir de 3 sans preuve suffisante, rien n'est envoyé :
   * l'API refuserait en 422. La ligne reste visiblement « à compléter » et part
   * dès que la preuve tient les vingt caractères.
   */
  const choisitNiveau = useCallback(
    (ligne: LigneVue, niveau: number) => {
      const id = ligne.technologieId;
      const preuve = brouillons[id] ?? ligne.note?.preuve ?? '';

      if (niveau >= SEUIL_PREUVE && !preuveSuffisante(preuve)) {
        majNote(id, {
          statut: 'notee',
          niveau,
          preuve: preuve || null,
          reportee: ligne.note?.reportee ?? false,
          confirmee: true,
          complete: false,
          miseAJourLe: new Date().toISOString(),
        });
        setEtats((e) => ({
          ...e,
          [id]: { etat: 'preuve_attendue', message: `Evidence required (${PREUVE_MIN} characters)` },
        }));
        // Déplier le champ et y placer le curseur : c'est là que la suite se joue.
        requestAnimationFrame(() => preuvesRef.current[id]?.focus());
        return;
      }

      void enregistre(ligne, 'notee', niveau, niveau >= SEUIL_PREUVE ? preuve : preuve || null);
    },
    [brouillons, enregistre, majNote],
  );

  const marqueNonConcerne = useCallback(
    (ligne: LigneVue) => {
      void enregistre(ligne, 'non_concernee', null, null);
    },
    [enregistre],
  );

  const saisitPreuve = useCallback(
    (ligne: LigneVue, texte: string) => {
      const id = ligne.technologieId;
      setBrouillons((b) => ({ ...b, [id]: texte }));

      const niveau = ligne.note?.niveau ?? null;
      const statut = ligne.note?.statut ?? 'notee';
      if (statut !== 'notee' || niveau === null) return;

      window.clearTimeout(minuteurs.current[id]);

      if (niveau >= SEUIL_PREUVE && !preuveSuffisante(texte)) {
        setEtats((e) => ({
          ...e,
          [id]: {
            etat: 'preuve_attendue',
            message: `${PREUVE_MIN - texte.trim().length} more character(s)`,
          },
        }));
        return;
      }

      // Enregistrement à la ligne, sans bouton Enregistrer (§6.1).
      minuteurs.current[id] = window.setTimeout(() => {
        void enregistre(ligne, 'notee', niveau, texte.trim() || null);
      }, 600);
    },
    [enregistre],
  );

  // --- Groupes et lignes affichées ---------------------------------------

  const groupes = fiche?.groupes ?? [];
  const groupe: GroupeVue | undefined =
    groupes.find((g) => g.cle === groupeActif) ?? groupes[0];

  const lignesAffichees: LigneVue[] = useMemo(() => {
    if (!fiche) return [];
    const terme = filtre.trim().toLowerCase();
    if (terme.length > 0) {
      return fiche.groupes
        .flatMap((g) => g.technologies)
        .filter((t) => t.titre.toLowerCase().includes(terme) || t.nom.includes(terme))
        .slice(0, 60);
    }
    return groupe?.technologies ?? [];
  }, [fiche, filtre, groupe]);

  useEffect(() => {
    setIndexFocus(0);
  }, [groupeActif, filtre]);

  // --- Clavier ------------------------------------------------------------

  const deplace = useCallback(
    (delta: number) => {
      setIndexFocus((index) =>
        Math.min(Math.max(0, index + delta), Math.max(0, lignesAffichees.length - 1)),
      );
    },
    [lignesAffichees.length],
  );

  // La ligne au clavier doit rester visible. Effet séparé : déplacer le focus
  // est un état, faire défiler est un effet de bord.
  useEffect(() => {
    lignesRef.current[indexFocus]?.scrollIntoView?.({ block: 'nearest' });
  }, [indexFocus]);

  /**
   * Le clavier est branché sur la grille, pas sur la fenêtre : une frappe qui
   * arrive alors que le focus est ailleurs — barre d'adresse, extension, autre
   * onglet de l'interface — ne doit pas noter une technologie à l'insu de la
   * personne. La grille prend le focus au montage, donc `0`–`5` marchent tout
   * de suite, sans clic.
   */
  const surTouche = useCallback(
    (evenement: React.KeyboardEvent<HTMLElement>): void => {
      if (evenement.ctrlKey || evenement.metaKey || evenement.altKey) return;

      const cible = evenement.target as HTMLElement | null;
      const dansUnChamp =
        cible?.tagName === 'TEXTAREA' || cible?.tagName === 'INPUT' || cible?.tagName === 'SELECT';

      if (evenement.key === '/' && !dansUnChamp) {
        evenement.preventDefault();
        champRecherche.current?.focus();
        return;
      }

      if (dansUnChamp) {
        // Dans la preuve, Entrée valide la ligne et passe à la suivante.
        if (evenement.key === 'Enter' && cible?.tagName === 'TEXTAREA' && !evenement.shiftKey) {
          evenement.preventDefault();
          (cible as HTMLTextAreaElement).blur();
          grille.current?.focus();
          deplace(1);
        }
        return;
      }

      if (fiche?.lectureSeule) return;
      const ligne = lignesAffichees[indexFocus];
      if (!ligne) return;

      if (/^[0-5]$/.test(evenement.key)) {
        evenement.preventDefault();
        choisitNiveau(ligne, Number(evenement.key));
        return;
      }
      if (evenement.key.toLowerCase() === 'n') {
        evenement.preventDefault();
        marqueNonConcerne(ligne);
        return;
      }
      if (evenement.key === 'Enter' || evenement.key === 'ArrowDown' || evenement.key === 'j') {
        evenement.preventDefault();
        deplace(1);
        return;
      }
      if (evenement.key === 'ArrowUp' || evenement.key === 'k') {
        evenement.preventDefault();
        deplace(-1);
      }
    },
    [fiche?.lectureSeule, lignesAffichees, indexFocus, choisitNiveau, marqueNonConcerne, deplace],
  );

  // La grille prend le focus dès qu'elle est prête : le clavier doit marcher
  // sans clic préalable, c'est tout l'intérêt (§6.1).
  useEffect(() => {
    if (fiche) grille.current?.focus({ preventScroll: true });
  }, [fiche]);

  // --- Actions de masse ---------------------------------------------------

  async function lanceActionMasse(
    action: 'non_concerne' | 'confirmer_reports',
    cle: string,
  ): Promise<void> {
    if (!trimestre) return;
    setActionMasse(undefined);
    try {
      const resultat = await appelApi<ResultatActionMasse>(`/api/campagnes/${trimestre}/notes`, {
        methode: 'PUT',
        corps: { action, groupe: cle },
      });
      await charge();
      setActionMasse(
        action === 'non_concerne'
          ? `${resultat.touchees} line(s) marked “not applicable”${resultat.ignorees > 0 ? `, ${resultat.ignorees} already answered and left untouched` : ''}.`
          : `${resultat.touchees} carry-over(s) confirmed${resultat.ignorees > 0 ? `, ${resultat.ignorees} skipped for lack of sufficient evidence` : ''}.`,
      );
    } catch (cause) {
      setActionMasse(cause instanceof Error ? cause.message : 'Bulk action failed');
    }
  }

  async function soumet(): Promise<void> {
    if (!trimestre) return;
    try {
      await appelApi(`/api/campagnes/${trimestre}/soumettre`, { methode: 'POST' });
      await charge();
      await session.rafraichit();
      setSoumission('Profile submitted. You can still edit it until the campaign closes.');
    } catch (cause) {
      setSoumission(cause instanceof Error ? cause.message : 'Could not submit');
    }
  }

  // --- Rendu --------------------------------------------------------------

  if (!trimestre) {
    return (
      <div className="contenu section">
        <Message variante="attention">
          No campaign is open at the moment. You will be notified in Teams when the next quarter
          opens.
        </Message>
      </div>
    );
  }

  if (erreurChargement) {
    return (
      <div className="contenu section">
        <Message variante="erreur">{erreurChargement}</Message>
      </div>
    );
  }

  if (!fiche) return <Chargement quoi="Loading your profile" />;

  const reste = joursRestants(fiche.campagne.echeance);

  return (
    <>
      {/* --- Bandeau collant ---------------------------------------------- */}
      <div className="bandeau">
        <div className="contenu bandeau__interieur">
          <div className="bandeau__bloc">
            <div>
              <p className="eyebrow">My profile · {fiche.campagne.trimestre}</p>
              <p className="discret" style={{ marginTop: 2 }}>
                {fiche.lectureSeule
                  ? `Campaign ${fiche.campagne.etat === 'fermee' ? 'closed' : fiche.campagne.etat} — read only`
                  : `Due ${dateLongue(fiche.campagne.echeance)}${reste !== null && reste >= 0 ? ` · ${reste} days left` : ''}`}
              </p>
            </div>

            <div className="bandeau__compteur">
              <span className="bandeau__valeur">
                {fiche.progression.repondu} / {fiche.progression.total} answered
              </span>
              <Jauge progression={fiche.progression} />
            </div>

            {fiche.progression.aRevoir > 0 ? (
              <Badge variante="report">{fiche.progression.aRevoir} to review</Badge>
            ) : null}
            {fiche.progression.incompletes > 0 ? (
              <Badge variante="incomplet">{fiche.progression.incompletes} without evidence</Badge>
            ) : null}
          </div>

          <div className="barre-actions">
            <input
              ref={champRecherche}
              type="search"
              className="saisie"
              style={{ width: 190 }}
              placeholder="Search  /"
              value={filtre}
              onChange={(e) => setFiltre(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setFiltre('');
                  grille.current?.focus();
                }
              }}
              aria-label="Search for a technology"
            />
            {fiche.lectureSeule ? null : (
              <button type="button" className="bouton bouton--rose" onClick={() => void soumet()}>
                {fiche.soumiseLe ? 'Update' : 'Submit'}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="contenu saisie-grille">
        {/* --- Colonne des groupes -------------------------------------- */}
        <nav className="colonne-groupes" aria-label="Technology groups">
          {groupes.map((g, index) => {
            const nouveauRadar = index === 0 || groupes[index - 1]!.radar !== g.radar;
            const complet = g.progression.repondu >= g.progression.total;
            return (
              <div key={g.cle}>
                {nouveauRadar ? <p className="groupe-radar">{g.libelleRadar}</p> : null}
                <button
                  type="button"
                  className={`groupe-bouton${g.cle === groupe?.cle ? ' groupe-bouton--actif' : ''}${complet ? ' groupe-bouton--complet' : ''}`}
                  onClick={() => {
                    setFiltre('');
                    setGroupeActif(g.cle);
                  }}
                >
                  <span>{g.libelle}</span>
                  <span className="groupe-bouton__compteur">
                    {g.progression.repondu}/{g.progression.total}
                  </span>
                </button>
              </div>
            );
          })}

          <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <p className="champ__libelle" style={{ marginBottom: 10 }}>
              Keyboard
            </p>
            <div className="raccourcis" style={{ flexDirection: 'column', gap: 6 }}>
              <span>
                <kbd>0</kbd>–<kbd>5</kbd> rate
              </span>
              <span>
                <kbd>N</kbd> not applicable
              </span>
              <span>
                <kbd>↵</kbd> next line
              </span>
              <span>
                <kbd>/</kbd> search
              </span>
            </div>
          </div>
        </nav>

        {/* --- Lignes ---------------------------------------------------- */}
        <section
          ref={grille}
          tabIndex={-1}
          onKeyDown={surTouche}
          className="grille-saisie"
          aria-label="Technologies to rate"
        >
          {soumission ? (
            <div style={{ marginBottom: 20 }}>
              <Message variante="succes">{soumission}</Message>
            </div>
          ) : null}
          {actionMasse ? (
            <div style={{ marginBottom: 20 }}>
              <Message variante="info">{actionMasse}</Message>
            </div>
          ) : null}
          {fiche.lectureSeule ? (
            <div style={{ marginBottom: 20 }}>
              <Message variante="attention">
                Campaign {fiche.campagne.trimestre} is{' '}
                {fiche.campagne.etat === 'fermee' ? 'closed' : fiche.campagne.etat}. No changes are
                possible, not even by an administrator.
              </Message>
            </div>
          ) : null}

          <div className="entete-groupe">
            <div>
              <p className="eyebrow" style={{ marginBottom: 8 }}>
                {filtre ? 'Results' : groupe?.libelleRadar}
              </p>
              <h2 style={{ fontSize: 'clamp(22px, 2.4vw, 30px)' }}>
                {filtre ? `« ${filtre} »` : (groupe?.libelle ?? '—')}
              </h2>
              <p className="discret" style={{ marginTop: 6 }}>
                {filtre
                  ? `${lignesAffichees.length} technolog${lignesAffichees.length === 1 ? 'y' : 'ies'} found, across all groups`
                  : `${groupe?.progression.repondu ?? 0} of ${groupe?.progression.total ?? 0} answered in this group`}
              </p>
            </div>

            {!filtre && groupe && !fiche.lectureSeule ? (
              <div className="barre-actions">
                <button
                  type="button"
                  className="bouton bouton--petit"
                  onClick={() => void lanceActionMasse('non_concerne', groupe.cle)}
                  title="Marks every line in this group that is still unanswered as “not applicable”"
                >
                  All “not applicable”
                </button>
                {groupe.progression.aRevoir > 0 ? (
                  <button
                    type="button"
                    className="bouton bouton--petit"
                    onClick={() => void lanceActionMasse('confirmer_reports', groupe.cle)}
                  >
                    Confirm {groupe.progression.aRevoir} carry-over(s)
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>

          {lignesAffichees.length === 0 ? (
            <p className="vide">No technology here.</p>
          ) : null}

          {lignesAffichees.map((ligne, index) => {
            const note = ligne.note;
            const brouillon = brouillons[ligne.technologieId] ?? note?.preuve ?? '';
            const etat = etats[ligne.technologieId];
            const incomplete = noteIncomplete(note, brouillons[ligne.technologieId]);
            const montrePreuve =
              note?.statut === 'notee' && note.niveau !== null && note.niveau >= SEUIL_PREUVE;

            return (
              <div
                key={ligne.technologieId}
                ref={(element) => {
                  lignesRef.current[index] = element;
                }}
                className={`ligne${index === indexFocus ? ' ligne--focus' : ''}${incomplete ? ' ligne--incomplete' : ''}`}
                onMouseDown={() => setIndexFocus(index)}
              >
                <div className="ligne__identite">
                  <span className="ligne__titre">
                    {ligne.titre}
                    {ligne.nouvelle ? <Badge variante="nouveau">New</Badge> : null}
                    {note?.reportee && !note.confirmee ? (
                      <Badge variante="report" titre="Carried over from last quarter, to review">
                        Carried over
                      </Badge>
                    ) : null}
                    {incomplete ? <Badge variante="incomplet">Evidence</Badge> : null}
                  </span>
                  <span className="ligne__meta">
                    <Anneau anneau={ligne.anneau} />
                    {filtre ? <span className="discret">{ligne.radar}</span> : null}
                    <span
                      className={`indicateur-sauvegarde${etat?.etat === 'erreur' ? ' indicateur-sauvegarde--erreur' : ''}`}
                    >
                      {etat?.etat === 'en_cours' ? 'saving…' : null}
                      {etat?.etat === 'enregistre' ? 'saved' : null}
                      {etat?.etat === 'preuve_attendue' ? etat.message : null}
                      {etat?.etat === 'erreur' ? etat.message : null}
                      {!etat && note ? `rated ${dateLongue(note.miseAJourLe)}` : null}
                    </span>
                  </span>
                </div>

                <div className="notes" role="group" aria-label={`Level for ${ligne.titre}`}>
                  {[0, 1, 2, 3, 4, 5].map((niveau) => {
                    const choisi = note?.statut === 'notee' && note.niveau === niveau;
                    const echelon = fiche.echelle.find((e) => e.niveau === niveau);
                    return (
                      <button
                        key={niveau}
                        type="button"
                        className={`note-bouton${choisi ? ' note-bouton--choisi' : ''}`}
                        aria-pressed={choisi}
                        disabled={fiche.lectureSeule}
                        title={echelon ? `${niveau} — ${echelon.titre} : ${echelon.definition}` : undefined}
                        onClick={() => choisitNiveau(ligne, niveau)}
                      >
                        {niveau}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    className={`note-bouton note-bouton--nc${note?.statut === 'non_concernee' ? ' note-bouton--choisi' : ''}`}
                    aria-pressed={note?.statut === 'non_concernee'}
                    disabled={fiche.lectureSeule}
                    title="This technology is not part of my job — different from a 0"
                    onClick={() => marqueNonConcerne(ligne)}
                  >
                    not applicable
                  </button>
                </div>

                {montrePreuve ? (
                  <div className="ligne__preuve">
                    <textarea
                      ref={(element) => {
                        preuvesRef.current[ligne.technologieId] = element;
                      }}
                      className={`zone-texte${incomplete ? ' zone-texte--incomplete' : ''}`}
                      placeholder="Which project, when, and what did you do on it?"
                      value={brouillon}
                      readOnly={fiche.lectureSeule}
                      onChange={(e) => saisitPreuve(ligne, e.target.value)}
                      rows={2}
                    />
                    <div className="ligne__preuve-aide">
                      <span>
                        Required from {SEUIL_PREUVE} up — {PREUVE_MIN} characters minimum.
                      </span>
                      <span className="mono">
                        {brouillon.trim().length} / {PREUVE_MIN}
                      </span>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}

          {!fiche.lectureSeule && fiche.progression.nonRepondu > 0 ? (
            <p className="discret" style={{ marginTop: 24 }}>
              {fiche.progression.nonRepondu} line(s) still unanswered. You can submit anyway: a
              form that blocks on {fiche.progression.total} lines never gets filled in.
            </p>
          ) : null}
        </section>
      </div>
    </>
  );
}
